import { useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { tokens } from "../../../theme";
import type { IStreamFormData } from "../../../types/streams";
import { usePackagesAndServices } from "../../bookings/add-booking2/Components/usePackagesAndServices";
import type { BookingType } from "../../bookings/add-booking2/AddBookingV2";
import type { Package } from "../../../types/package";

interface StreamPackageSelectionProps {
  formData: IStreamFormData;
  setFormData: React.Dispatch<React.SetStateAction<IStreamFormData>>;
  // The booking's API type string, e.g. "FUNERAL" / "CONFERENCE".
  bookingType: string;
}

// The booking-add hook keys off the lowercase BookingType union; the stream
// carries the uppercase API string, so normalise before querying packages.
const toBookingType = (apiType: string): BookingType =>
  (apiType || "OTHER").toLowerCase() as BookingType;

const StreamPackageSelection: React.FC<StreamPackageSelectionProps> = ({
  formData,
  setFormData,
  bookingType,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const accent = colors.primary[400];

  const { packages, additionalServicesConfig, loading, error } =
    usePackagesAndServices(toBookingType(bookingType));

  const selectedServiceIds = formData.package_service_ids ?? [];

  const handleSelectPackage = useCallback(
    (pkg: Package) => {
      setFormData((prev) => ({
        ...prev,
        package: {
          id: pkg.id,
          name: pkg.name,
          price: pkg.price,
          contents: pkg.contents,
          base_services: pkg.base_services,
        },
        package_item_id: null,
      }));
    },
    [setFormData],
  );

  const handleToggleService = useCallback(
    (serviceId: number) => {
      setFormData((prev) => {
        const current = prev.package_service_ids ?? [];
        const next = current.includes(serviceId)
          ? current.filter((id) => id !== serviceId)
          : [...current, serviceId];
        return { ...prev, package_service_ids: next };
      });
    },
    [setFormData],
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography sx={{ color: "error.main", py: 2 }}>
        Failed to load packages: {error}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Package
        </Typography>
        {packages.length === 0 ? (
          <Typography sx={{ color: "text.secondary" }}>
            No packages available for this booking type.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 3,
              alignItems: "stretch",
            }}
          >
            {packages.map((pkg) => {
              const isSelected = formData.package?.id === pkg.id;
              return (
                <Card
                  key={pkg.id}
                  onClick={() => handleSelectPackage(pkg)}
                  sx={{
                    borderRadius: "16px",
                    border: isSelected
                      ? `2px solid ${accent}`
                      : "1px solid",
                    borderColor: isSelected ? accent : "divider",
                    cursor: "pointer",
                    backgroundColor: "background.paper",
                    boxShadow: isSelected ? `0px 8px 24px ${accent}22` : "none",
                    transition: "all 0.2s ease-in-out",
                    height: "100%",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      borderColor: accent,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {pkg.name}
                      </Typography>
                      {isSelected && (
                        <CheckCircleIcon sx={{ color: accent }} fontSize="small" />
                      )}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ color: accent, fontWeight: 700, mb: 1 }}
                    >
                      ${Number(pkg.price ?? 0).toFixed(2)}
                    </Typography>
                    {pkg.contents?.slice(0, 5).map((line, i) => (
                      <Typography
                        key={i}
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        • {line}
                      </Typography>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Additional Services
        </Typography>
        {Object.keys(additionalServicesConfig).length === 0 ? (
          <Typography sx={{ color: "text.secondary" }}>
            No additional services available.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, 1fr)",
              },
              gap: 3,
            }}
          >
            {Object.entries(additionalServicesConfig).map(
              ([category, items]) => (
                <Card
                  key={category}
                  sx={{
                    borderRadius: "12px",
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      {category}
                    </Typography>
                    {items.map((item) => {
                      const serviceId = Number(item.value);
                      return (
                        <FormControlLabel
                          key={item.value}
                          control={
                            <Checkbox
                              checked={selectedServiceIds.includes(serviceId)}
                              onChange={() => handleToggleService(serviceId)}
                              sx={{ color: accent, "&.Mui-checked": { color: accent } }}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {item.label}
                              {item.price ? ` — $${Number(item.price).toFixed(2)}` : ""}
                            </Typography>
                          }
                          sx={{ display: "flex", ml: 0 }}
                        />
                      );
                    })}
                  </CardContent>
                </Card>
              ),
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StreamPackageSelection;
